import Checkbox from "@/common/components/Checkbox"
import MaskedTextField from "@/common/components/MaskedTextField"
import TextField from "@/common/components/TextField"
import { cpfMask, phoneMask } from "@/common/constants/masks.constant"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { ContactData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { UseMutateFunction, useQueryClient } from "@tanstack/react-query"
import { Control, Controller, FieldErrors, UseFormRegister } from "react-hook-form"
import ResendLeadContactEmailModal from "../../../common/components/ResendLeadContactEmailModal"
import { ContactState } from "../../publicCompanyLeadsFunnel/types/publicCompanyLead"
import type { PrivateCompanyLeadsFunnelUpdateContactValidationSchema } from "../schemas/update.schema"

interface ContactPersonalInformationSectionProps {
  privateCompanyLeadId?: string;
  contact?: ContactData
  register: UseFormRegister<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>
  control: Control<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>
  errors: FieldErrors<PrivateCompanyLeadsFunnelUpdateContactValidationSchema>
  showReviewButtons?: boolean
  isPending?: boolean
  approveContact: UseMutateFunction<any, Error, { description?: string } | undefined, unknown>
  declineContact: UseMutateFunction<any, Error, { description?: string } | undefined, unknown>
  disableFields?: boolean
}

export const ContactPersonalInformationSection = ({
  contact,
  privateCompanyLeadId,
  register,
  control,
  errors,
  showReviewButtons,
  isPending,
  approveContact,
  declineContact,
  disableFields,
}: ContactPersonalInformationSectionProps) => {
  const queryClient = useQueryClient();

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex justify-between">
        <p className="text-lg font-bold">Dados pessoais</p>

        {showReviewButtons && (
          <ReviewButtons
            reviewState={contact?.last_review?.state}
            onApprove={approveContact}
            onDecline={declineContact}
            isPending={isPending}
          />
        )}
      </div>
      {contact?.last_review?.state === 'disapproved' && (
        <RequestedReview description={contact?.last_review.description} />
      )}
      <div className="w-full flex gap-2">
        <TextField
          label="Nome"
          className="w-2/3"
          defaultValue={contact?.full_name}
          errorMessage={errors?.contacts?.full_name?.message || undefined}
          {...register('contacts.full_name')}
          disabled={disableFields}
          markAsRequired
        />
        <TextField
          label="Cargo"
          maxLength={32}
          className="w-1/3"
          defaultValue={contact?.role_in_company}
          errorMessage={errors?.contacts?.role_in_company?.message || undefined}
          {...register('contacts.role_in_company')}
          disabled={disableFields}
          markAsRequired
        />
      </div>

      <div className="w-full flex gap-2">
        <Controller
          control={control}
          name="contacts.cpf"
          render={({ field }) => (
            <MaskedTextField
              label="CPF"
              mask={cpfMask}
              className="w-2/3"
              defaultValue={contact?.cpf}
              errorMessage={errors?.contacts?.cpf?.message || undefined}
              {...field}
              disabled={disableFields}
              markAsRequired
            />
          )}
        />

        <Controller
          control={control}
          name="contacts.phone.number"
          render={({ field }) => (
            <MaskedTextField
              label="Telefone"
              className="w-1/3"
              mask={phoneMask}
              defaultValue={contact?.phone?.number}
              errorMessage={errors?.contacts?.phone?.number?.message || undefined}
              {...field}
              disabled={disableFields}
              markAsRequired
            />
          )}
        />
      </div>

      <div className="flex gap-4 items-center">
        <TextField
          label="Email"
          className="w-full"
          value={contact?.email}
          errorMessage={errors?.contacts?.email?.message || undefined}
          {...register('contacts.email')}
          disabled={disableFields}
          markAsRequired
        />

        {contact?.state === ContactState.awaiting_client_documents && (
          <ResendLeadContactEmailModal contact={contact} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })} />
        )}
      </div>

      <Checkbox
        label="Preenche Dados Empresariais (Expectativa de crédito, serviços)"
        errorMessage={errors?.contacts?.responsible_for_company_information?.message || undefined}
        {...register('contacts.responsible_for_company_information')}
        disabled={disableFields}
      />
    </div>
  )
}
